import { admin2 } from '@/api/instance'

export const GetSignatureTemplate = async ({
  shopId,
  start,
  limit,
  sourceType,
  name,
  enableApplyScope,
}) => {
  return await admin2({
    method: 'get',
    url: `${shopId}/signatureTemplate`,
    params: {
      start,
      limit,
      sourceType,
      name,
      enableApplyScope,
    },
  })
}

export const GetSignatureTemplateCount = async ({
  shopId,
  sourceType,
  name,
  enableApplyScope,
}) => {
  return await admin2({
    method: 'get',
    url: `${shopId}/signatureTemplate/count`,
    params: {
      sourceType,
      name,
      enableApplyScope,
    },
  })
}

export const FindSignatureTemplate = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'get',
    url: `${shopId}/signatureTemplate/${id}`,
  })
}
/**
 * 新增簽名同意書 admin.signatureTemplate.create
 * @param {Object} param0
 * @param {boolean} param0.enable - 是否啟用
 * @param {string} param0.name - 名稱
 * @param {string} param0.sourceType - 來源類型
 * @param {boolean} param0.enableApplyScope - 是否啟用應用範圍
 * @param {Array<{ type: string, originIds: string[], itemNos: string[], itemNames: string[] }>} param0.applyScopeSetting - 應用範圍設定
 * @param {string} param0.expirationMode - 過期模式
 * @param {{ value: number, unit: string }} param0.expirationPeriodSetting - 過期期間設定
 * @param {number} param0.order - 排序
 * @param {string} param0.content - 內容
 * @param {Array<SignatureTemplateField>} param0.fields - 欄位
 * @param {boolean} param0.isNotDeletable - 是否不可刪除
 * @param {number} param0.signDeadlineDays - 簽署截止天數
 * @returns {import('@/api/instance').Api2Response}
 */
export const CreateSignatureTemplate = async ({
  shopId,
  enable,
  name,
  sourceType,
  enableApplyScope,
  applyScopeSetting,
  expirationMode,
  expirationPeriodSetting,
  order,
  content,
  fields,
  isNotDeletable,
  signDeadlineDays,
}) => {
  return await admin2({
    method: 'POST',
    url: `${shopId}/signatureTemplate`,
    data: {
      enable,
      name,
      sourceType,
      enableApplyScope,
      applyScopeSetting,
      expirationMode,
      expirationPeriodSetting,
      order,
      content,
      fields,
      isNotDeletable,
      signDeadlineDays,
    },
  })
}

/**
 * 更新簽名同意書 admin.signatureTemplate.update
 * @param {Object} param0
 * @param {string} param0.shopId - 商店ID
 * @param {string} param0.id - 同意書範本ID
 * @param {boolean} param0.enable - 是否啟用
 * @param {string} param0.name - 名稱
 * @param {string} param0.sourceType - 來源類型
 * @param {boolean} param0.enableApplyScope - 是否啟用應用範圍
 * @param {Array<{ type: string, originIds: string[], itemNos: string[], itemNames: string[] }>} param0.applyScopeSetting - 應用範圍設定
 * @param {string} param0.expirationMode - 過期模式
 * @param {{ value: number, unit: string }} param0.expirationPeriodSetting - 過期期間設定
 * @param {number} param0.order - 排序
 * @param {string} param0.content - 內容
 * @param {Array<SignatureTemplateField>} param0.fields - 欄位
 * @param {boolean} param0.isNotDeletable - 是否不可刪除
 * @param {number} param0.signDeadlineDays - 簽署截止天數
 * @returns {import('@/api/instance').Api2Response}
 */
export const UpdateSignatureTemplate = async ({
  shopId,
  id,
  enable,
  name,
  sourceType,
  enableApplyScope,
  applyScopeSetting,
  expirationMode,
  expirationPeriodSetting,
  order,
  content,
  fields,
  isNotDeletable,
  signDeadlineDays,
}) => {
  return await admin2({
    method: 'put',
    url: `${shopId}/signatureTemplate/${id}`,
    data: {
      enable,
      name,
      sourceType,
      enableApplyScope,
      applyScopeSetting,
      expirationMode,
      expirationPeriodSetting,
      order,
      content,
      fields,
      isNotDeletable,
      signDeadlineDays,
    },
  })
}

export const DeleteSignatureTemplate = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'delete',
    url: `${shopId}/signatureTemplate/${id}`,
  })
}

export const ViewSignatureTemplateApplyScopeSetting = async ({
  shopId,
  applyScopeSetting,
}) => {
  return await admin2({
    method: 'post',
    url: `${shopId}/signatureTemplate/viewApplyScopeSetting`,
    data: {
      applyScopeSetting,
    },
  })
}

export const GetSignatureTemplateCreationInfo = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `${shopId}/signatureTemplate/getCreationInfo`,
  })
}

/**
 * OCR 辨識同意書欄位 admin.signatureTemplate.ocrFields
 * @param {Object} params - 請求參數
 * @param {string} params.shopId - 商店ID
 * @param {File} params.file - 上傳的檔案 (pdf/jpg/jpeg/png)
 * @returns {import('@/api/instance').Api2Response<{ content: string, fields: Array<SignatureTemplateField> }>} OCR 辨識結果 { content, fields }
 */
export const OcrSignatureTemplateFields = async ({
  shopId,
  file,
}) => {
  const formData = new FormData()
  formData.append('file', file)
  return await admin2({
    method: 'POST',
    url: `${shopId}/signatureTemplate/ocrFields`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

/**
 * 預覽同意書 admin.signatureTemplate.preview
 * @param {Object} params - 請求參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.content - 同意書 HTML 內容
 * @param {Array<SignatureTemplateField>} params.fields - 同意書欄位定義
 * @returns {import('@/api/instance').Api2Response<Object>} 預覽結果
 */
export const PreviewSignatureTemplate = async ({
  shopId,
  content,
  fields,
}) => {
  return await admin2({
    method: 'POST',
    url: `${shopId}/signatureTemplate/preview`,
    data: {
      content,
      fields,
    },
  })
}

/**
 * @typedef {Object} SignatureTemplateField
 * @property {string} fieldType - 欄位類型
 * @property {string} label - 欄位標籤
 * @property {boolean} isRequired - 是否必填
 * @property {string} fieldKey - 欄位鍵值
 * @property {string | null} systemFieldKey - 系統欄位鍵值
 * @property {string | null} notes - 備註
 */

/**
 * @typedef {Object} SignatureTemplateFieldOptions
 * @property {Array<SignatureTemplateField>} defaultFields - 預設欄位列表
 * @property {Array<SignatureTemplateField>} templateFields - 範本欄位列表
 */

/**
 * 取得同意書欄位選項 admin.signatureTemplate.getFieldOptions
 * @param {Object} params - 請求參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.id - 同意書範本ID
 * @returns {import('@/api/instance').Api2Response<SignatureTemplateFieldOptions>} 欄位選項資料
 * @example
 * {
 *   defaultFields: [
 *     {
 *       fieldType: 'text',
 *       label: '姓名',
 *       isRequired: true,
 *       fieldKey: 'name',
 *       systemFieldKey: 'member.name',
 *       notes: null,
 *     },
 *   ],
 *   templateFields: [
 *     {
 *       fieldType: 'text',
 *       label: '同意內容',
 *       isRequired: false,
 *       fieldKey: 'consentContent',
 *       systemFieldKey: null,
 *       notes: null,
 *     },
 *   ],
 * }
 */
export const GetSignatureTemplateFieldOptions = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `${shopId}/signatureTemplate/${id}/fieldOptions`,
  })
}

/**
 * 取得同意書預設欄位選項 admin.signatureTemplate.getDefaultFieldsOptions
 * @param {Object} params - 請求參數
 * @param {string} params.shopId - 商店ID
 * @returns {import('@/api/instance').Api2Response<{ defaultFields: Array<SignatureTemplateField> }>} 預設欄位選項
 */
export const GetSignatureTemplateDefaultFieldOptions = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `${shopId}/signatureTemplate/defaultFieldOptions`,
  })
}

// admin.appointmentOrder.getSignatureRecordCreationInfo
export const GetSignatureRecordCreationInfo = async ({
  shopId,
  orders,
  effectiveAt,
  memberId,
}) => {
  return await admin2({
    method: 'POST',
    url: `${shopId}/appointmentOrder/getSignatureRecordCreationInfo`,
    data: {
      orders,
      effectiveAt,
      memberId,
    },
  })
}
